import { ApolloError } from '@apollo/client';

import { useErpSurveysListQuery } from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { ISurveyListItem } from './surveyModel';

// Stable empty fallback: a fresh `[]` per render would give react-table a new
// `data` reference each time (see the stable-refetch note below).
const EMPTY_SURVEYS: ISurveyListItem[] = [];

interface ISurveysFilters {
  courseId?: string;
  includeArchived?: boolean;
  search?: string;
}

interface ISurveysHook {
  surveys: ISurveyListItem[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Spec 0011 — survey template list (`surveys` query). `courseId` narrows to
 * that course's templates PLUS universal ones (what the attach popup needs);
 * archived rows are hidden unless `includeArchived`. Plain list, no
 * pagination — templates number in the dozens. Filter changes re-run the
 * query through Apollo's variable tracking.
 */
export const useSurveys = (filters: ISurveysFilters = {}): ISurveysHook => {
  const { data, loading, error, refetch } = useErpSurveysListQuery({
    variables: {
      courseId: filters.courseId || undefined,
      includeArchived: filters.includeArchived ?? false,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Apollo's `refetch` identity can change between renders; a bare
  // `() => refetch()` wrapper would too. The consumer feeds this into the
  // mutation hook, whose callbacks flow into the react-table `columns` memo —
  // an unstable identity there makes `columns` a new array every render, and
  // react-table's core `useColumnVisibility` effect (keyed on `[columns]`)
  // then dispatches resetHiddenColumns on every commit → infinite render loop
  // ("Maximum update depth exceeded"). The shared ref-backed wrapper pins it.
  const stableRefetch = useStableRefetch(refetch);

  return {
    surveys: data?.surveys ?? EMPTY_SURVEYS,
    loading,
    error,
    refetch: stableRefetch,
  };
};
